/* jshint esversion: 6 */
/* jshint ignore:start */
import { useContext, useEffect, useRef, useState } from "react";
import { MyContext } from "../Context/MyProvider";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import { showToast } from "../showTost";

function loadCashfreeSdk() {
    return new Promise((resolve, reject) => {
        if (window.Cashfree) {
            resolve(window.Cashfree);
            return;
        }
        const existing = document.getElementById("cashfree-js-sdk");
        if (existing) {
            existing.addEventListener("load", () => resolve(window.Cashfree));
            existing.addEventListener("error", reject);
            return;
        }
        const script = document.createElement("script");
        script.id = "cashfree-js-sdk";
        script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
        script.async = true;
        script.onload = () => resolve(window.Cashfree);
        script.onerror = reject;
        document.body.appendChild(script);
    });
}

const CashfreePay = ({ Amount }) => {
    const { ApiBaseUrl, setApiLoading, setPaymentStatus, setTransactionId } = useContext(MyContext);
    const started = useRef(false);
    const [phase, setPhase] = useState("loading"); // loading | open | verifying | error | done
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
        if (!Amount || started.current) return;
        started.current = true;

        const Userid = secureLocalStorage.getItem("Userid");
        setApiLoading(true);
        setPhase("loading");

        let cancelled = false;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const verifyAndComplete = async (orderId) => {
            setPhase("verifying");
            try {
                const verify = await axios.get(`${ApiBaseUrl}/customer/cashfree-return`, {
                    params: { status: "0", order_id: orderId },
                });
                if (cancelled) return;
                if (verify.data?.status) {
                    setTransactionId(verify.data.transactionId || orderId);
                    setPaymentStatus("PaymentDone");
                    setPhase("done");
                } else {
                    setPaymentStatus("PaymentFaild");
                    setErrorMsg(verify.data?.message || "Payment not completed");
                    setPhase("error");
                    showToast({ title: verify.data?.message || "Payment failed", id: "error" });
                }
            } catch (err) {
                if (cancelled) return;
                console.error("Cashfree verify error:", err);
                setPaymentStatus("PaymentFaild");
                setErrorMsg("Could not verify payment");
                setPhase("error");
                showToast({ title: "Could not verify payment", id: "error" });
            }
        };

        axios
            .post(`${ApiBaseUrl}/customer/cashfree-payment`, {
                amount: Amount,
                uid: Userid,
                status: "1",
            })
            .then(async (res) => {
                setApiLoading(false);
                if (cancelled) return;
                if (!res.data.status || !res.data.payment_session_id) {
                    setPhase("error");
                    setErrorMsg(res.data.message || "Cashfree payment could not start");
                    showToast({
                        title: res.data.message || "Cashfree payment could not start",
                        id: "error",
                    });
                    return;
                }

                const orderId = res.data.order_id;
                try {
                    const Cashfree = await loadCashfreeSdk();
                    const mode = res.data.environment === "production" ? "production" : "sandbox";
                    const cashfree = Cashfree({ mode });
                    setPhase("open");

                    // On-page modal overlay (stays on RapidMediK — no full-page leave)
                    const result = await cashfree.checkout({
                        paymentSessionId: res.data.payment_session_id,
                        redirectTarget: "_modal",
                    });

                    if (cancelled) return;

                    if (result?.error) {
                        setPhase("error");
                        setErrorMsg(result.error.message || "Checkout closed");
                        setPaymentStatus("PaymentFaild");
                        showToast({ title: result.error.message || "Payment closed", id: "info" });
                        return;
                    }

                    if (result?.paymentDetails) {
                        await verifyAndComplete(orderId);
                    } else if (result?.redirect) {
                        // Rare bank/UPI hard redirect — return_url handles it
                        setPhase("open");
                    } else {
                        // Modal closed without paymentDetails — treat as cancel
                        setPhase("error");
                        setErrorMsg("Payment was not completed");
                        setPaymentStatus("PaymentFaild");
                    }
                } catch (err) {
                    console.error("Cashfree checkout error:", err);
                    setPhase("error");
                    setErrorMsg("Cashfree checkout failed to open");
                    showToast({ title: "Cashfree checkout failed to open", id: "error" });
                }
            })
            .catch((error) => {
                setApiLoading(false);
                if (cancelled) return;
                console.error(error);
                setPhase("error");
                setErrorMsg("Cashfree payment request failed");
                showToast({ title: "Cashfree payment request failed", id: "error" });
            });

        return () => {
            cancelled = true;
            document.body.style.overflow = prevOverflow || "auto";
        };
    }, [Amount, ApiBaseUrl, setApiLoading, setPaymentStatus, setTransactionId]);

    const showOverlay = phase === "loading" || phase === "open" || phase === "verifying";

    return (
        <>
            {showOverlay && (
                <div
                    className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/55 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Secure payment"
                >
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md px-6 py-8 text-center">
                        <div className="mx-auto mb-4 h-10 w-10 rounded-full border-2 border-[#0d9488] border-t-transparent animate-spin" />
                        <h3 className="text-lg font-semibold text-gray-900">
                            {phase === "verifying" ? "Confirming payment…" : "Secure payment"}
                        </h3>
                        <p className="text-sm text-gray-500 mt-2">
                            {phase === "verifying"
                                ? "Please wait while we verify your payment."
                                : "Cashfree checkout is open in the popup. Complete payment there — you stay on RapidMediK."}
                        </p>
                        <p className="text-xs text-gray-400 mt-4">Amount · ₹{Amount}</p>
                    </div>
                </div>
            )}

            {phase === "error" && (
                <p className="text-sm text-center text-red-600 py-4">{errorMsg || "Payment error"}</p>
            )}
            {phase === "done" && (
                <p className="text-sm text-center text-green-600 py-4">Payment successful</p>
            )}
            {(phase === "loading" || phase === "open") && !showOverlay && (
                <p className="text-sm text-center text-gray-500 py-6">Opening payment…</p>
            )}
        </>
    );
};

export default CashfreePay;
/* jshint ignore:end */
